import PrimaryCtaType from '~/constants/PrimaryCtaType';

type Params = {
  ctaType: PrimaryCtaType;
  isInExclusivePreview: boolean;
  isOutOfArea: boolean;
  isReservedByOtherAndSubscribed: boolean;
  isReservedByUserAndKicked: boolean;
  isSold: boolean;
  purchasePending: boolean;
  stopSaleRecall: boolean;
};

export type VehicleDescription = {
  description: string | undefined;
  scenario: string | undefined;
};

export default function deriveVehicleDescription({
  ctaType,
  isInExclusivePreview,
  isOutOfArea,
  isReservedByOtherAndSubscribed,
  isReservedByUserAndKicked,
  isSold,
  purchasePending,
  stopSaleRecall
}: Params): VehicleDescription {
  if (isSold)
    return {
      description: 'This vehicle has been sold and is no longer available',
      scenario: 'Sold'
    };
  if (stopSaleRecall)
    return {
      description:
        'Sale of this vehicle is temporarily paused due to a safety recall',
      scenario: 'Stop sale recall'
    };
  if (isOutOfArea)
    return {
      description:
        "This vehicle cannot be delivered to the customer's location",
      scenario: 'Out of market'
    };

  switch (ctaType) {
    case PrimaryCtaType.GetStarted:
      return {
        description:
          'Vehicle is available and the customer can begin purchasing',
        scenario: 'Available'
      };
    case PrimaryCtaType.DisabledGetStarted:
      return {
        description:
          "Vehicle exists but cannot be purchased in the customer's delivery area",
        scenario: 'Available (restricted)'
      };
    case PrimaryCtaType.ContinuePurchase:
      return isInExclusivePreview
        ? {
            description:
              'This vehicle is in a private preview period and not yet publicly available for purchase',
            scenario: 'Exclusive preview'
          }
        : {
            description:
              'The customer is currently in the process of purchasing this vehicle and can continue where they left off',
            scenario: 'This user purchasing'
          };
    case PrimaryCtaType.CancelPreOrder:
      return {
        description:
          'The customer has reserved this vehicle which is still being prepared for sale',
        scenario: 'This user reserved (WIP)'
      };
    case PrimaryCtaType.Reservable:
      return {
        description:
          'This vehicle is not yet ready for sale but the customer can reserve priority to purchase it when available',
        scenario: 'Pre-order (WIP, unowned)'
      };
    case PrimaryCtaType.DisabledPreOrder:
      return {
        description:
          'This vehicle is not yet ready for sale and pre-order reservations are not currently available',
        scenario: 'Pre-order (WIP, unowned)'
      };
    case PrimaryCtaType.GetAlerts:
      return {
        description:
          'Another customer has reserved this vehicle — it is not available but the customer can sign up for alerts',
        scenario: 'Someone else has it on hold'
      };
    case PrimaryCtaType.ViewSimilarVehicles:
      if (isReservedByUserAndKicked)
        return {
          description:
            'The customer previously reserved this vehicle but their reservation was cancelled',
          scenario: 'WIP reservation kicked'
        };
      if (purchasePending)
        return {
          description:
            'Another customer is actively in the process of purchasing this vehicle — it is not available',
          scenario: 'Someone else purchasing'
        };
      if (isReservedByOtherAndSubscribed)
        return {
          description:
            'Another customer has reserved this vehicle — the customer has already signed up for availability alerts',
          scenario: 'Someone else has it on hold'
        };

      return {
        description: 'This vehicle is not currently available to purchase',
        scenario: 'Out of market'
      };
    case PrimaryCtaType.Default:
    case PrimaryCtaType.NotifyMe:
    case PrimaryCtaType.Reservation:
      return { description: undefined, scenario: undefined };
    default: {
      const ignored: never = ctaType;

      return { description: undefined, scenario: undefined };
    }
  }
}
